import { useCheckPermission } from "@/hook/use-check-permission"
import { triggerRevalidate } from "@/services/redis-cache"
import { t } from "@/languages/i18n"
import {
  checkSlugCategory,
  createCategory,
  createCategoryLocalization,
  deleteCategory,
  fetchCategories,
  getCategoryById,
  getCategoryDetail,
  updateCategory,
} from "@/services/category"
import { useDomainStore } from "@/stores/use-domain-store"
import { AttributesCategory, CategoryData, CategoryDataWithLocale, CategoryLocalizationPayload, Pagination, UseCategoryOptions, UseCategoryReturn } from "@/types/types"
import { getEnforcedDomainValue } from "@/utils/admin-domain-filter"
import { generateAndValidateSlug } from "@/utils/slug-utils"
import { App, TablePaginationConfig } from "antd"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"

export const useCategory = (options: UseCategoryOptions = {}): UseCategoryReturn => {
  const { searchTerm = "", filters = {}, refreshKey, autoFetch = true, initialPageSize = 10, sortField: viewSortField, sortOrder: viewSortOrder, listPopulate } = options
  const { message } = App.useApp();
  // Guards: reqSeq drops out-of-order responses, inFlightRequestKeyRef collapses
  // duplicate concurrent requests (React 19 StrictMode double-invoke).
  const reqSeq = useRef(0)
  const inFlightRequestKeyRef = useRef<string | null>(null)
  // Populate the visible columns need (Configure-the-view); ref keeps the fetch
  // callback stable while reading the latest value.
  const listPopulateRef = useRef(listPopulate)
  listPopulateRef.current = listPopulate
  const [categories, setCategories] = useState<CategoryData[]>([])
  const [category, setCategory] = useState<CategoryData | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [pagination, setPagination] = useState<Pagination>({
    page: 1,
    pageSize: initialPageSize,
    current: 1,
    total: 0,
  })
  const { hasPermission: checkPermission, permissions } = useCheckPermission()
  const { selectedDomain } = useDomainStore()
  const enforcedDomain = getEnforcedDomainValue(selectedDomain)
  // Sort chosen ad-hoc by clicking a column header; overrides the "Configure
  // view" default sort until cleared. Kept separate from viewSortField so a
  // header click doesn't get clobbered by viewConfig on the next render.
  const [sortParam, setSortParam] = useState<string | undefined>(undefined)

  const stableFilters = useMemo(
    () => ({
      ...filters,
      domain: enforcedDomain || filters.domain || "",
    }),
    [filters.name, filters.type, filters.domain, filters.locale, filters.state, enforcedDomain],
  )

  useEffect(() => {
    setPagination(prev =>
      prev.page === 1 && prev.current === 1 && prev.pageSize === initialPageSize
        ? prev
        : { ...prev, page: 1, current: 1, pageSize: initialPageSize },
    )
    // Reset to page 1 whenever the filters or the "Configure view" page-size/sort
    // change. pageSize/sort come straight from viewConfig (single source of truth);
    // there is no separate "sync page size" effect any more (that caused the
    // double fetch). See VIEW_CONFIG_FIXES_ROUND2.md RC1.
  }, [stableFilters, initialPageSize, viewSortField, viewSortOrder]);

  const fetchCategoriesData = useCallback(
    async (searchParams: { [key: string]: string } = {}, publicationState: string = "live") => {
      const params: { [key: string]: any } = {
        populate: "localizations",
        ...searchParams
      }

      // Ad-hoc column-header sort wins; otherwise "Configure view" default;
      // otherwise createdAt:desc.
      if (!params.sort) {
        params.sort = sortParam ?? (viewSortField ? `${viewSortField}:${viewSortOrder ?? "desc"}` : "createdAt:desc")
      }

      // Configure-the-view: populate exactly what the visible columns need
      // (baseline mirrors "localizations"; adds `parents` when that column is on).
      if (listPopulateRef.current) {
        params.populate = listPopulateRef.current
      }

      if (stableFilters.name) {
        params.name = stableFilters.name
      } else if (searchTerm) {
        params.name = searchTerm
      }

      if (stableFilters.type) {
        params.type = stableFilters.type
      }

      if (stableFilters.domain && searchParams.domain === undefined) {
        params.domain = stableFilters.domain
      }

      if (params.domain === "") {
        delete params.domain
      }

      if (stableFilters.locale && stableFilters.locale !== "") {
        params.locale = stableFilters.locale
      } else if (!params.locale) {
        params.locale = "all";
      }

      let publicationStateToUse = publicationState
      if (stableFilters.state === "published") {
        publicationStateToUse = "live"
      } else if (stableFilters.state === "draft") {
        publicationStateToUse = "preview"
        params.publishedAtNull = "true"
      } else {
        publicationStateToUse = "preview"
      }

      if (!params.page) {
        params.page = String(pagination.page)
      }
      if (!params.pageSize) {
        // pageSize is owned by "Configure view" — never read back from response.
        params.pageSize = String(initialPageSize)
      }

      const requestKey = JSON.stringify({ params, publicationStateToUse })
      if (inFlightRequestKeyRef.current === requestKey) {
        return
      }
      inFlightRequestKeyRef.current = requestKey
      const seq = ++reqSeq.current

      try {
        setLoading(true)
        setError(null)
        const response = await fetchCategories(params, publicationStateToUse)
        if (seq !== reqSeq.current) return // superseded by a newer request

        if (response?.error) {
          setError(response.error.message)
          return response
        }

        setCategories(response?.data?.length > 0 ? response.data : [])
        // Only the total/current come from the response — pageSize stays owned by
        // viewConfig so responses can't feed a fetch loop (RC2).
        setPagination(prev => ({
          ...prev,
          total: response?.meta?.pagination?.total ?? 0,
          current: response?.meta?.pagination?.page ?? 1,
        }))

        return response
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : "Lỗi không xác định."
        setError(errorMessage)
        message.error(t("Lỗi khi tải danh sách danh mục. Vui lòng thử lại."))
        throw err
      } finally {
        if (inFlightRequestKeyRef.current === requestKey) {
          inFlightRequestKeyRef.current = null
        }
        if (seq === reqSeq.current) setLoading(false)
      }
    },
    [searchTerm, stableFilters, pagination.page, initialPageSize, viewSortField, viewSortOrder, sortParam],
  )

  const fetchCategoryByIdData = useCallback(async (id: number): Promise<CategoryData | null> => {
    try {
      setLoading(true)
      setError(null)
      const response = await getCategoryById(id)

      if (response?.data) {
        setCategory(response.data)
        return response.data
      }

      return null
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Lỗi không xác định."
      setError(errorMessage)
      message.error(t("Lỗi khi tải thông tin danh mục. Vui lòng thử lại."))
      return null
    } finally {
      setLoading(false)
    }
  }, [])

  const createCategoryData = useCallback(async (data: AttributesCategory): Promise<boolean> => {
    try {
      setLoading(true)
      setError(null)
      await createCategory(data)
      message.success(t("Tạo danh mục thành công!"))
      return true
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Lỗi không xác định."
      setError(errorMessage)
      message.error(t("Lỗi khi tạo danh mục. Vui lòng thử lại."))
      return false
    } finally {
      setLoading(false)
    }
  }, [])

  const updateCategoryData = useCallback(async (id: string, data: any): Promise<boolean> => {
    try {
      setLoading(true)
      setError(null)
      await updateCategory(id, data)
      triggerRevalidate("categoryId", id)
      message.success(t("Cập nhật danh mục thành công!"))
      return true
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Lỗi không xác định."
      setError(errorMessage)
      message.error(t("Lỗi khi cập nhật danh mục. Vui lòng thử lại."))
      return false
    } finally {
      setLoading(false)
    }
  }, [])

  const deleteCategoryData = useCallback(
    async (id: number): Promise<boolean> => {
      try {
        setLoading(true)
        setError(null)
        await deleteCategory(id)
        message.success(t("Xóa danh mục thành công!"))
        await fetchCategoriesData()
        return true
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : "Lỗi không xác định"
        setError(errorMessage)
        message.error(t("Xóa danh mục thất bại. Vui lòng thử lại"))
        return false
      } finally {
        setLoading(false)
      }
    },
    [fetchCategoriesData],
  )

  const duplicateCategory = useCallback(
    async (id: number): Promise<boolean> => {
      try {
        setLoading(true)
        const res = await getCategoryDetail(id)
        const item = res?.data
        if (!item) return false

        const baseName = item.attributes?.name || ""
        const copyName = `${baseName} Copy`
        const { slug } = await generateAndValidateSlug(copyName, checkSlugCategory)

        const payload: any = {
          name: copyName,
          slug,
          description: item.attributes?.description || "",
          ...(item.attributes?.domain ? { domain: item.attributes.domain } : {}),
          ...(item.attributes?.type ? { type: item.attributes.type as any } : {}),
          ...(item.attributes?.playlistUrl ? { playlistUrl: item.attributes.playlistUrl } : {}),
          publishedAt: item.attributes?.publishedAt ? new Date(item.attributes.publishedAt).toISOString() : undefined,
          ...(item.attributes?.locale ? { locale: item.attributes.locale } : {}),
        }

        await createCategory(payload)
        message.success(t("Nhân bản danh mục thành công!"))

        const publicationState = stableFilters.state === "published" ? "live" : "preview"
        const extraParams: Record<string, string> = {}
        if (stableFilters.state === "draft") {
          extraParams.publishedAtNull = "true"
        }
        await fetchCategoriesData(extraParams, publicationState)
        return true
      } catch (e) {
        message.error(t("Lỗi khi nhân bản danh mục. Vui lòng thử lại."))
        return false
      } finally {
        setLoading(false)
      }
    },
    [fetchCategoriesData, stableFilters.state],
  )

  const resolveLocalizationTarget = useCallback(
    async (record: CategoryData, locale: string): Promise<number | undefined> => {
      try {
        if (!locale) return undefined;

        const recordWithLocale = record as CategoryDataWithLocale;
        const currentLocale = recordWithLocale.attributes.locale;

        if (currentLocale === locale) {
          return record.id;
        }

        const localization = recordWithLocale.attributes.localizations?.data.find(
          (item: CategoryData): boolean => {
            const itemWithLocale = item as CategoryDataWithLocale;
            return itemWithLocale.attributes.locale === locale;
          }
        );

        return localization?.id;
      } catch (e) {
        message.error(t("Không thể xác định bản dịch. Vui lòng thử lại."));
        return undefined;
      }
    },
    [t]
  );

  const handleTranslateCategory = useCallback(
    async (
      values: any,
      targetLocale: string,
      originalId: number
    ): Promise<{ success: boolean }> => {
      try {
        setLoading(true);
        const payload: CategoryLocalizationPayload = {
          name: values.name,
          slug: values.slug,
          domain: values.domain || "tamdagroup.eu",
          type: values.type || "post",
          description: values.description || "",
          playlistUrl: values.playlistUrl || "",
          locale: targetLocale,
          publishedAt: values.status === "Published" ? new Date().toISOString() : null,
          seo: {
            metaTitle: values.metaTitle,
            metaDescription: values.metaDescription,
            preventIndexing: values.preventIndexing || false,
            structuredData: values.structuredData,
          },
        };

        const response = await createCategoryLocalization(originalId, payload);
        if (response?.data) {
          message.success(t("Tạo bản dịch thành công!"));
          return { success: true };
        }
        return { success: false };
      } catch (error) {
        message.error(t("Lỗi khi tạo bản dịch. Vui lòng thử lại."));
        return { success: false };
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const handleTableChange = useCallback((paginationConfig: TablePaginationConfig, _filters?: unknown, sorter?: unknown) => {
    // Only the page changes here — pageSize is governed by "Configure view".
    setPagination(prev => ({
      ...prev,
      page: paginationConfig.current || 1,
      current: paginationConfig.current || 1,
    }))

    const s = sorter as { field?: string | (string | number)[]; order?: "ascend" | "descend" } | undefined
    if (s && !Array.isArray(s) && s.field && s.order) {
      const field = Array.isArray(s.field) ? s.field[s.field.length - 1] : s.field
      const order = s.order === "ascend" ? "asc" : "desc"
      setSortParam(`${field}:${order}`)
    } else if (s && !Array.isArray(s) && !s.order) {
      setSortParam(undefined)
    }

    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [])

  useEffect(() => {
    if (!autoFetch) return
    fetchCategoriesData({}, "preview")
  }, [autoFetch, fetchCategoriesData, refreshKey, listPopulate])

  const handleDelete = useCallback(
    async (id: number): Promise<boolean> => {
      return await deleteCategoryData(id)
    },
    [deleteCategoryData],
  )

  const hasPermission = useCallback(
    (action: "create" | "update" | "delete"): boolean => {
      return checkPermission("category", action)
    },
    [checkPermission],
  )

  return {
    categories,
    category,
    loading,
    error,
    pagination,
    fetchCategories: fetchCategoriesData,
    fetchCategoryById: fetchCategoryByIdData,
    createCategory: createCategoryData,
    updateCategory: updateCategoryData,
    deleteCategory: deleteCategoryData,
    duplicateCategory,
    resolveLocalizationTarget,
    handleTableChange,
    handleDelete,
    permissions,
    hasPermission,
    handleTranslateCategory,
  }
}
